import { FLAKINESS_FLAKY } from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import type { PartialEvalCase, PartialEvalRun } from "~/services/evals";
import type { CaseHealthRow } from "./cases";
import { type ResultsByRun, runDurationSeconds } from "./core";
import { isNumber, median } from "./math";
import { type RunSummary, summarizeRun } from "./runs";

/** The headline figures over a suite and its recent runs. */
export interface EvalKpis {
  /** The newest run that judged anything, and its summary. */
  latestRun: PartialEvalRun | null;
  latestSummary: RunSummary | null;
  /** Percentage points against the previous judged run, or null without one. */
  passRateDeltaPoints: number | null;
  /** Mean case score of the latest judged run, on the 1-4 rubric. */
  latestMeanScore: number | null;
  /** Score points against the previous judged run, or null without one. */
  scoreDelta: number | null;
  /**
   * Median wall-clock duration across completed runs in the window. A run's own
   * time, not the sum of its cases' — see the note at the top of this file.
   */
  medianRunDurationSeconds: number | null;
  caseCount: number;
  /**
   * Cases whose pass rate moves at least `FLAKINESS_FLAKY` run to run, over each
   * case's own history: a case is flaky independently of how often it was selected.
   */
  flakyCaseCount: number;
  /** Cases judged over their own history that never passed in it. */
  brokenCaseCount: number;
}
/**
 * Headline numbers for the KPI row. `runs` is oldest-first, as elsewhere.
 *
 * Two windows on purpose: the run figures are properties of whole runs and read
 * `resultsByRun`, while the two case figures read `health`, which is windowed per
 * case — "is this case flaky" is a question about the case's own history.
 */
export function buildKpis(
  cases: PartialEvalCase[],
  runs: PartialEvalRun[],
  resultsByRun: ResultsByRun,
  health: CaseHealthRow[]
): EvalKpis {
  const judgedRuns = runs.filter(
    (run) => summarizeRun(resultsByRun[run.id] ?? []).passRate != null
  );
  const latestRun = judgedRuns.at(-1) ?? null;
  const previousRun = judgedRuns.at(-2) ?? null;
  const latestSummary = latestRun ? summarizeRun(resultsByRun[latestRun.id] ?? []) : null;
  const previousSummary = previousRun ? summarizeRun(resultsByRun[previousRun.id] ?? []) : null;

  const passRateDeltaPoints =
    latestSummary?.passRate != null && previousSummary?.passRate != null
      ? (latestSummary.passRate - previousSummary.passRate) * 100
      : null;

  const scoreDelta =
    latestSummary?.meanScore != null && previousSummary?.meanScore != null
      ? latestSummary.meanScore - previousSummary.meanScore
      : null;

  return {
    latestRun,
    latestSummary,
    passRateDeltaPoints,
    latestMeanScore: latestSummary?.meanScore ?? null,
    scoreDelta,
    medianRunDurationSeconds: median(runs.map(runDurationSeconds).filter(isNumber)),
    caseCount: cases.length,
    flakyCaseCount: health.filter((row) => (row.flakiness ?? 0) >= FLAKINESS_FLAKY).length,
    brokenCaseCount: health.filter((row) => row.judgedCount > 0 && row.passedCount === 0).length,
  };
}
